import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
    VStack,
    HStack,
    Text,
    Input,
    Select,
    NumberInput,
    NumberInputField,
    Button,
    Divider,
    Box,
} from '@chakra-ui/react';
import { useAppStore } from '../../state/store';
import { ManageDialogFrame, ManageDialogCloseFooter } from '../manage/ManageDialogFrame';
import { structuralLoadManager } from '../../core/services';
import { LoadCaseType, LoadCombinationMethod } from '../../core/StructuralLoadManager';
import { useAnalysisStore } from '../../state/useAnalysisStore';

const CASE_TYPES: LoadCaseType[] = ['Dead', 'Live', 'Wind', 'Seismic', 'Snow', 'Rain', 'Other'];
const METHODS: LoadCombinationMethod[] = ['LRFD', 'ASD', 'Eurocode'];

/**
 * Analyze > Load Cases & Combinations dialog (BIM "Reorder" / Analysis
 * Load Combinations): manage load cases, generate standard code-specified
 * combinations and create custom ones.
 */
export function LoadCasesDialog() {
    const { t } = useTranslation();
    const isOpen = useAppStore((s) => s.manageDialog === 'analyzeLoadCases');
    const onClose = useAppStore((s) => s.closeManageDialog);
    // Version bump after every mutation to force re-read of the manager.
    const [, force] = useState(0);
    const refresh = () => force(v =>v + 1); const [caseName, setCaseName] = useState(''); const [caseType, setCaseType] = useState<LoadCaseType>('Live'); const [method, setMethod] = useState<LoadCombinationMethod>('LRFD');
    const [comboName, setComboName] = useState('');
    const [comboFactors, setComboFactors] = useState('Dead:1.2, Live:1.6');

    const cases = structuralLoadManager.getAllLoadCases();
    const combos = structuralLoadManager.getAllCombinations();
    const activeName = useAnalysisStore((s) => s.activeLoadCaseName);
    const setActiveName = useAnalysisStore((s) => s.setActiveLoadCaseName);

    const addCase = () => {
        const name = caseName.trim();
        if (!name) return;
        if (structuralLoadManager.getLoadCaseByName(name)) return;
        structuralLoadManager.createLoadCase(name, caseType, caseType === 'Dead' ? 1 : 0);
        setCaseName('');
        refresh();
    };

    const generateStandard = () => {
        structuralLoadManager.generateStandardCombinations(method);
        refresh();
    };

    const addCustomCombo = () => {
        const name = comboName.trim() || comboFactors.trim();
        if (!name || !comboFactors.trim()) return;
        const factors: Record<string, number> = {};
        comboFactors.split(',').forEach(pair => {
            const [k, v] = pair.split(':');
            if (k && v && Number.isFinite(Number(v))) factors[k.trim()] = Number(v);
        });
        if (Object.keys(factors).length === 0) return;
        structuralLoadManager.createLoadCombination(name, method, factors);
        setComboName('');
        refresh();
    };

    return (
        <ManageDialogFrame title={t('analyze.loadCasesCombinations')} isOpen={isOpen} onClose={onClose} size="lg">
            <VStack spacing={4} align="stretch">
                <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>{t('analyze.loadCases')}</Text>
                    <HStack spacing={2} mb={2} flexWrap="wrap">
                        {cases.map(lc => (
                            <Button
                                key={lc.id}
                                size="xs"
                                variant={activeName === lc.name ? 'solid' : 'outline'}
                                colorScheme={activeName === lc.name ? 'blue' : 'gray'}
                                onClick={() => setActiveName(lc.name)}
                            >
                                {lc.name} ({lc.type})
                            </Button>
                        ))}
                    </HStack>
                    <Text fontSize="xs" color="var(--color-text-secondary)" mb={2}>Selected case is the target for Point/Line/Area load placement.</Text>
                    <HStack>
                        <Input size="sm" placeholder={t('analyze.newCaseName')} value={caseName} onChange={e => setCaseName(e.target.value)} maxW="180px" />
                        <Select size="sm" value={caseType} onChange={e => setCaseType(e.target.value as LoadCaseType)} maxW="130px">
                            {CASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </Select>
                        <Button size="sm" colorScheme="blue" onClick={addCase} isDisabled={!caseName.trim()}>{t('analyze.addCase')}</Button>
                    </HStack>
                </Box>

                <Divider />

                <Box>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>{t('analyze.loadCombinations')}</Text>
                    <VStack align="stretch" spacing={1} maxH="180px" overflowY="auto" mb={2}>
                        {combos.length === 0 && <Text fontSize="xs" color="var(--color-text-secondary)">{t('analyze.noCombinationsYetGenerateAStandardSetOrAddACustomOne')}</Text>}
                        {combos.map(c => (
                            <HStack key={c.id} justify="space-between">
                                <Text fontSize="xs">{c.name}</Text>
                                <Text fontSize="xs" color="var(--color-text-secondary)">{c.method}</Text>
                            </HStack>
                        ))}
                    </VStack>
                    <HStack mb={3}>
                        <Text fontSize="xs" w="110px">{t('analyze.standard')}</Text>
                        <Select size="sm" value={method} onChange={e => setMethod(e.target.value as LoadCombinationMethod)} maxW="140px">
                            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                        </Select>
                        <Button size="sm" colorScheme="green" onClick={generateStandard}>{t('analyze.generate')}</Button>
                    </HStack>
                    <HStack>
                        <Input size="sm" placeholder={t('analyze.combinationName')} value={comboName} onChange={e => setComboName(e.target.value)} maxW="180px" />
                        <Input
                            size="sm"
                            placeholder={t('analyze.factorsDead12Live16')}
                            value={comboFactors}
                            onChange={e => setComboFactors(e.target.value)}
                            flex={1}
                        />
                        <Button size="sm" colorScheme="blue" onClick={addCustomCombo}>{t('analyze.addCombo')}</Button>
                    </HStack>
                </Box>
            </VStack>
        </ManageDialogFrame>
    );
}
